// packages import
import { InteractionType, PublicClientApplication } from '@azure/msal-browser';
import { useMsal } from "@azure/msal-react";
import { AuthCodeMSALBrowserAuthenticationProvider } from '@microsoft/microsoft-graph-client/authProviders/authCodeMsalBrowser';
import { createContext, FC, Reducer, useContext, useEffect, useMemo, useReducer, useState } from "react";
import { Outlet } from "react-router-dom";
// others
import config from "../../config";
import { useFetchAllRolesLazyQuery } from "../../generated";
import { getUser } from "../../graphService";
import { GRAPHQL_QUERY_POLICY_CACHE_FIRST } from "../constants";
import { AppContextReducerType, ChildrenType, OutlookError, OutlookUser } from "../interfaceTypes";
import { titleize } from "../utils";
import { Action, ActionType, initialState, reducer, State } from "./AppContextReducer";
import { AuthContext } from "./AuthContext";

export const AppContext = createContext<AppContextReducerType>({
  ...initialState,
  dispatch: () => {
    return;
  },
});

export const AppContextProvider: FC<ChildrenType> = ({ children }): JSX.Element => {
  const { isLoggedIn } = useContext(AuthContext)
  const [state, dispatch] = useReducer<Reducer<State, Action>>(reducer, initialState);
  const store = useMemo(() => ({ ...state, dispatch }), [state]);
  const { rolesData } = state

  const msal = useMsal();
  const [outlookUser, setOutlookUser] = useState<OutlookUser | undefined>(undefined);
  const [outlookError, setOutlookError] = useState<OutlookError | undefined>(undefined);

  const [fetchAllRoles] = useFetchAllRolesLazyQuery({
    ...GRAPHQL_QUERY_POLICY_CACHE_FIRST,
    variables: undefined,
    onError() { },

    onCompleted(data) {
      if (data) {
        const { fetchAllRoles: { roles } } = data

        if (roles) {
          const userRoles = roles?.map(item => {
            const { role } = item || {}
            const labelText = role?.split("_").join(" ")
            const labelTextToAdd = titleize(labelText || "")
            return { label: labelTextToAdd, value: role }
          })

          dispatch({ type: ActionType.SET_ROLES_DATA, rolesData: userRoles })
        }
      }

    }
  });

  const outlookDisplayError = (message: string, debug?: string) => {
    setOutlookError({ message, debug });
  }

  const outlookClearError = () => setOutlookError(undefined)
  const outlookAuthProvider = new AuthCodeMSALBrowserAuthenticationProvider(
    msal.instance as PublicClientApplication,
    {
      account: msal.instance.getActiveAccount()!,
      scopes: config.scopes,
      interactionType: InteractionType.Popup
    }
  );

  useEffect(() => {
    const checkUser = async () => {
      if (!outlookUser) {
        try {
          // Check if user is already signed in
          const account = msal.instance.getActiveAccount();
          if (account) {
            // Get the user from Microsoft Graph
            const user = await getUser(outlookAuthProvider);

            setOutlookUser({
              displayName: user.displayName || '',
              email: user.mail || user.userPrincipalName || '',
              timeFormat: user.mailboxSettings?.timeFormat || 'h:mm a',
              timeZone: user.mailboxSettings?.timeZone || 'UTC'
            });
          }
        } catch (err: any) {
          const { message } = err
          dispatch({ type: ActionType.SET_OUTLOOK_DISPLAY_ERROR, outlookDisplayError: message })
        }
      }
    };
    checkUser(); // eslint-disable-next-line
  }, [outlookUser]);

  const outlookSignIn = async () => {
    await msal.instance.loginPopup({
      scopes: config.scopes,
      prompt: 'select_account'
    });

    // Get the user from Microsoft Graph
    const user = await getUser(outlookAuthProvider);

    setOutlookUser({
      displayName: user.displayName || '',
      email: user.mail || user.userPrincipalName || '',
      timeFormat: user.mailboxSettings?.timeFormat || '',
      timeZone: user.mailboxSettings?.timeZone || 'UTC'
    });
  };

  const outlookSignOut = async () => {
    await msal.instance.logoutRedirect({
      onRedirectNavigate: () => {
        return false;
      }
    });

    setOutlookUser(undefined);
  };

  useEffect(() => {
    isLoggedIn && !rolesData?.length && fetchAllRoles()
    // eslint-disable-next-line
  }, [isLoggedIn, rolesData])

  return (
    <AppContext.Provider value={{ ...store, outlookUser, outlookSignIn, outlookSignOut, outlookAuthProvider, outlookError, outlookDisplayError, outlookClearError }}>
      {children}

      <Outlet />
    </AppContext.Provider>
  );
}